import { IReviewerSearchProperties, ISearchQueryItems } from 'src/api/queries';

/**
 * Values used by the alembic TagInput (chip input). Tag fields are kept as
 * `{ text }[]` in the form and mapped to `string[]` when building the payload.
 */
export type ChipValue = { text: string };

/** Form value per field: single text for input/textarea, chips for tag. */
export type LexiconFormValue = string | ChipValue[];

/** Form state keyed by predicateLabel. */
export type LexiconFormValues = Record<string, LexiconFormValue>;

/** API payload `data` keyed by predicateLabel. */
export type LexiconData = Record<string, string | string[]>;

export const getPropertyStrings = (
  properties: ISearchQueryItems['properties'],
  propertyName: string
): string[] | undefined =>
  properties.find(p => p.propertyName === propertyName)?.propertyValueString;

/**
 * Canonical display order matching the reviewer card (name fields first, then
 * institution → email → degree → expertise → others). Unknown predicate labels
 * fall back to the end, preserving their schema order.
 */
export const REVIEWER_FORM_FIELD_ORDER = [
  'ReviewerSalutation',
  'ReviewerFirstName',
  'ReviewerMiddleName',
  'ReviewerLastName',
  'ReviewerPrimaryEmailAddress',
  'ReviewerInstitution',
  'ReviewerDepartment',
  'ReviewerCountryOrRegion',
  'ReviewerDegree',
  'ReviewerSpecialty',
  'ReviewerSubSpecialty',
  'ReviewerKeyword',
  'scandinavianKeywords',
  'ReviewerSecondaryEmailAddress',
  'ReviewerPosition',
  'ReviewerPeopleNotes',
  'ReviewerDetailedPeopleNotes',
  'ReviewerQualifications',
  'ReviewerOrigin',
  'ReviewerInterests',
  'ReviewerGenericNotes',
  'ReviewerBiography'
];

/** Fields to render in the add/edit form, driven by the merged lexicon config. */
export const getFormFields = (
  properties?: IReviewerSearchProperties[]
): IReviewerSearchProperties[] =>
  (properties ?? [])
    .filter(p => p.config?.fieldsForm === true)
    .sort((a, b) => {
      const aIndex = REVIEWER_FORM_FIELD_ORDER.indexOf(a.predicateLabel);
      const bIndex = REVIEWER_FORM_FIELD_ORDER.indexOf(b.predicateLabel);
      return (
        (aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex) -
        (bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex)
      );
    });

export const getControlType = (
  field: IReviewerSearchProperties
): 'input' | 'tag' | 'textarea' => field.config?.controlType ?? 'input';

export const getFieldLabel = (field: IReviewerSearchProperties): string =>
  field.config?.label ?? field.displayName ?? field.predicateLabel;

export const isTagField = (field: IReviewerSearchProperties): boolean =>
  getControlType(field) === 'tag';

const toChipValues = (values?: string[]): ChipValue[] =>
  (values ?? []).map(text => ({ text }));

const chipValuesToStrings = (chips: ChipValue[] = []): string[] =>
  chips.map(c => c.text.trim()).filter(Boolean);

export const buildEmptyFormValues = (
  fields: IReviewerSearchProperties[]
): LexiconFormValues =>
  fields.reduce<LexiconFormValues>((acc, field) => {
    acc[field.predicateLabel] = isTagField(field) ? [] : '';
    return acc;
  }, {});

export const buildFormValuesFromItem = (
  item: ISearchQueryItems,
  fields: IReviewerSearchProperties[]
): LexiconFormValues =>
  fields.reduce<LexiconFormValues>((acc, field) => {
    const values = getPropertyStrings(item.properties, field.predicateLabel);
    acc[field.predicateLabel] = isTagField(field)
      ? toChipValues(values)
      : values?.[0] ?? '';
    return acc;
  }, {});

/**
 * Builds the API `data` object. Empty values are dropped by default; pass
 * `includeEmpty` to send cleared fields as empty (empty string / empty array)
 * so the backend clears them on edit.
 */
export const toLexiconData = (
  values: LexiconFormValues,
  fields: IReviewerSearchProperties[],
  includeEmpty = false
): LexiconData =>
  fields.reduce<LexiconData>((acc, field) => {
    const value = values[field.predicateLabel];

    if (isTagField(field)) {
      const strings = chipValuesToStrings(value as ChipValue[]);
      if (includeEmpty || strings.length > 0) {
        acc[field.predicateLabel] = strings;
      }
    } else {
      const text = typeof value === 'string' ? value.trim() : '';
      if (includeEmpty || text.length > 0) {
        acc[field.predicateLabel] = text;
      }
    }

    return acc;
  }, {});
